/**
 * Work: every running briefing, and exactly where it stands.
 *
 * Spec 2026-09-20 (work-cards), confirmed section rebuilt per spec 2026-10-28
 * ("Client Work view shows the portal lifecycle read-only"). Two groups, one
 * screen:
 *
 *   To confirm   one row per submission the customer has not tapped yet, with
 *                the total price and a Confirm checkmark that opens the same
 *                PaySheet the estimate uses. Tapping the row itself opens the
 *                price block on Start (`#start/<submissionId>`). An unpriced
 *                submission says "Being priced" and offers Price again. Reads
 *                `/api/briefs` - a quote is not a work item yet.
 *   Confirmed    one Tracking-Card per work item - briefed AND briefless alike,
 *                the same source the Board reads (`GET /api/work`): the
 *                editor's first name once someone is on it, the title, ONE
 *                status line, a mono chip, and a 3-node rail
 *                Briefed → In production → Delivered. Tapping opens the board
 *                with that item's panel (`#work/board/<workItemId>`).
 *
 * `cards.ts` maps both reads to what a customer sees and is tested; this file
 * only draws.
 *
 * @module web/screens/app/ProjectsScreen
 */
import { useCallback, useEffect, useState } from "react";
import { get, post, raw } from "../../api/client.ts";
import type { BriefListResponse, ClientWorkItem, ClientWorkResponse, PortalBrief } from "../../api/types.ts";
import { fmtEurCompact } from "../../money.ts";
import { paySource } from "../../paySource.ts";
import { useBrand } from "../../state/BrandContext.tsx";
import { avatarColor, avatarInitial } from "./board/cards.ts";
import { BRIEFS_CHANGED, announceBriefsChanged, confirmResult, postConfirm } from "./confirmSheet.tsx";
import { NODES, isFlatRateRow, cardOfWorkItem, dayLabel, groupWork, repriceMessage, toConfirmLabel, type RepriceResult, type ToConfirmRow } from "./work/cards.ts";
import { SubmittedBy, submitterLabel } from "./work/SubmittedBy.tsx";
import { SwipeToConfirm, type SwipeResult } from "./work/SwipeToConfirm.tsx";

export function ProjectsScreen() {
  const { brand, refresh, notify } = useBrand();
  const [briefs, setBriefs] = useState<PortalBrief[] | null>(null);
  const [workItems, setWorkItems] = useState<ClientWorkItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [repricing, setRepricing] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [b, w] = await Promise.all([
        get<BriefListResponse>("/api/briefs"),
        get<ClientWorkResponse>("/api/work"),
      ]);
      setBriefs(b.briefs ?? []);
      setWorkItems(w.items ?? []);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
    const onChange = () => { void load(); };
    window.addEventListener(BRIEFS_CHANGED, onChange);
    return () => window.removeEventListener(BRIEFS_CHANGED, onChange);
  }, [load]);

  const dispatching = briefs?.some(b => b.submissionStatus === "confirmation_requested" || b.submissionStatus === "processing");
  useEffect(() => {
    if (!dispatching) return;
    const timer = window.setInterval(() => { void load(); }, 5000);
    return () => window.clearInterval(timer);
  }, [dispatching, load]);

  /**
   * The swipe's tap: the request and the sentences are shared with the quote
   * box (confirmSheet.tsx); server-side idempotency per child means a second
   * swipe settles onto the same charges and cards. Work adds the flat-rate
   * cases and its own reload.
   */
  async function settle(row: ToConfirmRow): Promise<SwipeResult> {
    const url = row.kind === "bundle"
      ? `/api/brief-submissions/${row.submissionId}/confirm`
      : `/api/briefs/${row.brief.id}/confirm`;
    const res = await postConfirm(url, row.kind === "bundle" ? row.amountDueCents : row.brief.fareCents);
    if (res.status === 409 && res.body?.code === "quote_changed") {
      await load();
      return { ok: false, message: "The price changed. Open this briefing and review the new quote." };
    }
    if (isFlatRateRow(row) && res.status === 409) {
      return { ok: false, message: res.body?.error ?? "Could not send this briefing. Refresh Work and try again." };
    }
    if (res.status === 200 || res.status === 202 || res.status === 409) {
      void refresh();
      // The checkmark gets its moment, THEN the card moves to its new group. The
      // announce also reloads this screen (its own BRIEFS_CHANGED listener), so
      // fired right away it unmounted the card after one GET, before the check showed.
      setTimeout(announceBriefsChanged, 1600);
      if (res.status === 202 && isFlatRateRow(row)) return { ok: true, message: "Sending to production" };
    } else if (isFlatRateRow(row)) {
      return { ok: false, message: res.body?.error ?? "Could not send to production. Try again." };
    }
    return confirmResult(res, row.kind === "bundle" && row.briefs.length > 1);
  }

  /**
   * Drop a briefing nobody confirmed. The server refuses anything confirmed or
   * charged, so the worst a stale card can do is show a sentence. On success we
   * just reload - the row is gone from /api/briefs and the nav badge recounts.
   */
  async function cancel(row: ToConfirmRow): Promise<void> {
    const url = row.kind === "bundle"
      ? `/api/brief-submissions/${row.submissionId}/cancel`
      : `/api/briefs/${row.brief.id}/cancel`;
    let res: { status: number; body: { error?: string } | null };
    try {
      res = await raw<{ error?: string }>(url, { method: "POST" });
    } catch {
      notify("No connection - try again in a moment.", "error");
      return;
    }
    if (res.status === 200) {
      announceBriefsChanged();
      void refresh();
      await load();
      notify(row.kind === "bundle" && row.briefs.length > 1 ? "Briefings cancelled." : "Briefing cancelled.", "success");
      return;
    }
    notify(res.body?.error ?? "Couldn't cancel it - try again in a moment.", "error");
    await load();
  }

  async function priceAgain(submissionId: string): Promise<void> {
    setRepricing(submissionId);
    try {
      const r = await post<RepriceResult>(`/api/brief-submissions/${submissionId}/reprice`);
      await load();
      const m = repriceMessage(r);
      notify(m.text, m.tone);
    } catch {
      notify("Couldn't price it this time - try again in a moment.", "error");
    } finally {
      setRepricing(null);
    }
  }

  const toConfirm = briefs ? groupWork(briefs).toConfirm : null;
  const ready = toConfirm !== null && workItems !== null;

  return (
    <>
      <header className="page-head fade-in">
        <h1 className="t-h2">Work</h1>
        <p className="t-body" style={{ marginTop: 4 }}>
          Every briefing you sent, and exactly where it stands right now.
        </p>
      </header>

      <div className="wk-list" style={{ marginTop: 28, maxWidth: 640 }}>
        {failed ? (
          <div className="t-body-sm" style={{ color: "var(--ink-3)" }}>
            Your work is temporarily unavailable. Nothing is lost - try again in a moment.
          </div>
        ) : !ready ? (
          <div className="t-body-sm" style={{ color: "var(--ink-3)" }}>Loading…</div>
        ) : toConfirm.length === 0 && workItems.length === 0 ? (
          <div className="proj-empty fade-in">
            <div className="peh">No briefings yet</div>
            <div className="pes">
              Send your first brief and it shows up here, from "finding your editor" to delivered.
            </div>
            <a className="btn btn-brand wk-empty-cta" href="#start">Request ads</a>
          </div>
        ) : (
          <>
            {toConfirm.length > 0 && (
              <section className="wk-group" data-group="to-confirm" aria-label="To confirm">
                <div className="proj-day fade-in">{toConfirm.every(isFlatRateRow) ? "Briefings" : "To confirm"}</div>
                {toConfirm.map(row => (
                  <ToConfirmCard
                    key={row.kind === "bundle" ? `sub-${row.submissionId}` : row.brief.id}
                    row={row}
                    brand={brand}
                    busy={row.kind === "bundle" && repricing === row.submissionId}
                    onConfirm={() => settle(row)}
                    onCancel={() => void cancel(row)}
                    onPriceAgain={row.kind === "bundle" ? () => void priceAgain(row.submissionId) : null}
                  />
                ))}
              </section>
            )}
            <Confirmed items={workItems} />
          </>
        )}
      </div>
    </>
  );
}

// ============================================================
// TO CONFIRM
// ============================================================

function ToConfirmCard({ row, brand, busy, onConfirm, onCancel, onPriceAgain }: {
  row: ToConfirmRow;
  brand: ReturnType<typeof useBrand>["brand"];
  busy: boolean;
  onConfirm: () => Promise<SwipeResult>;
  onCancel: () => void;
  onPriceAgain: (() => void) | null;
}) {
  const { title, line } = toConfirmLabel(row);
  const covered = isFlatRateRow(row);
  const [retrying, setRetrying] = useState(false);
  const [retryMessage, setRetryMessage] = useState<string | null>(null);
  async function retry() {
    setRetrying(true);
    const result = await onConfirm();
    setRetryMessage(result.message);
    setRetrying(false);
  }
  const due = row.kind === "bundle" ? row.amountDueCents : (row.brief.fareCents ?? 0);
  const href = !covered && row.kind === "bundle" ? `#start/${encodeURIComponent(row.submissionId)}` : null;
  const count = row.kind === "bundle" ? row.briefs.length : 1;
  // What the swipe will do with the money - the PaySheet's disclosure, on the card.
  const src = paySource(brand, due);
  return (
    <div
      className="wk wk-todo fade-in"
      data-submission-id={row.kind === "bundle" ? row.submissionId : undefined}
      data-brief-id={row.kind === "single" ? row.brief.id : undefined}
      data-confirmable={row.confirmable ? "1" : "0"}
    >
      <Tappable href={href} className="wk-row" label={`Open ${title}. ${submitterLabel(row.kind === "bundle" ? row.briefs[0]?.submittedBy : row.brief.submittedBy)}`}>
        <span className="wk-av todo" aria-hidden="true">{row.unpriced ? <ClockIcon /> : <ReceiptIcon />}</span>
        <span className="wk-main">
          <span className="wk-title">{title}</span>
          <SubmittedBy name={row.kind === "bundle" ? row.briefs[0]?.submittedBy : row.brief.submittedBy} />
          <span className="wk-line">{line}{count > 1 ? ` · ${count} cards` : ""}</span>
        </span>
        {/* A confirmable row shows its price ONCE, in the button that charges it
            (Alan 2026-09-21). The old €215 in the corner and the TO CONFIRM chip
            said the same two things the button already says, and the chip ended
            up sitting directly above Cancel, reading as its label. An unpriced
            row keeps the chip - there it is the only state on the card. */}
        {covered && <span className="wk-right"><span className="wk-chip">Included in plan</span></span>}
        {row.unpriced && (
          <span className="wk-right">
            <span className="wk-chip">Pricing</span>
          </span>
        )}
      </Tappable>
      {/* One row, two rails: the primary sits on the card's left edge, Cancel on
          its right, both a FIXED width so a €99 and a €1.215 briefing draw the
          same card (Alan 2026-09-21). The disclosure goes underneath, where it
          can wrap without pushing a button around. */}
      <div className="wk-actions">
        {covered && row.confirmable && <button type="button" className="btn btn-brand" disabled={retrying} onClick={() => void retry()}>{retrying ? "Sending…" : "Try again"}</button>}
        {!covered && row.confirmable && (
          <SwipeToConfirm
            action={count > 1 ? "Confirm all" : "Confirm"}
            amount={src.kind === "sponsored" ? "Sponsored" : fmtEurCompact(due)}
            disabled={!!src.blocked}
            onConfirm={onConfirm}
          />
        )}
        {row.unpriced && onPriceAgain && (
          <button type="button" className="wk-again" data-action="price-again" disabled={busy} onClick={onPriceAgain}>
            {busy ? "Pricing…" : "Price again"}
          </button>
        )}
        {(!covered || row.confirmable) && <CancelBriefing onCancel={onCancel} count={count} />}
      </div>
      {retryMessage && <p className="wk-disclosure" role="status">{retryMessage}</p>}
      {!covered && row.confirmable && (
        <p className="wk-disclosure">{src.kind === "no-card" ? src.note : `${src.label} · ${src.note}`}</p>
      )}
    </div>
  );
}

/**
 * Cancel, asked once in place - in a button that never changes size.
 *
 * Nothing has been charged yet, so this is not a destructive-modal moment - but
 * the customer did pay an assessor to price it, so a single stray click must not
 * take it. Two taps on the same small button, second one armed, no dialog, no
 * layer: the confirmation IS the button. It disarms itself after 4s so an armed
 * button never waits around for an unrelated click.
 *
 * It is quiet on purpose. One loud primary per screen, and that one is Confirm.
 * The armed label is ONE short word, not a sentence: the button has a fixed
 * width so the row cannot reflow under the cursor mid-decision, and "Cancel all
 * - tap again" simply does not fit a button that holds still.
 */
function CancelBriefing({ onCancel, count }: { onCancel: () => void; count: number }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button
      type="button"
      className={`wk-cancel${armed ? " armed" : ""}`}
      data-action={armed ? "cancel-confirm" : "cancel"}
      // The visible word is short so the button can hold its width; the full
      // sentence still reaches a screen reader, including the "all".
      aria-label={armed
        ? (count > 1 ? "Tap again to cancel all briefings" : "Tap again to cancel this briefing")
        : (count > 1 ? "Cancel all briefings" : "Cancel this briefing")}
      onClick={() => { if (armed) { setArmed(false); onCancel(); } else setArmed(true); }}
    >
      {armed ? "Sure?" : "Cancel"}
    </button>
  );
}

/**
 * The tappable face of a card. A real anchor when there is somewhere to go
 * (the price block), a plain row when there is not - an unconfirmed submission
 * still being priced must not pretend to open anything.
 */
function Tappable({ href, className, label, children }: {
  href: string | null;
  className: string;
  label: string;
  children: React.ReactNode;
}) {
  if (!href) return <div className={`${className} static`}>{children}</div>;
  return <a className={className} href={href} aria-label={label}>{children}</a>;
}

// ============================================================
// CONFIRMED — TRACKING-CARDS
// ============================================================

function Confirmed({ items }: { items: ClientWorkItem[] }) {
  if (items.length === 0) return null;
  const sorted = [...items].sort((a, b) => new Date(b.phaseAt).getTime() - new Date(a.phaseAt).getTime());
  const out: React.ReactNode[] = [];
  let lastDay: string | null = null;
  sorted.forEach((item, i) => {
    const day = dayLabel(new Date(item.phaseAt).getTime());
    if (day !== lastDay) {
      out.push(<div className="proj-day fade-in" key={`day-${day}`}>{day}</div>);
      lastDay = day;
    }
    out.push(<TrackingCard item={item} index={i} key={item.id} />);
  });
  return <section className="wk-group" data-group="confirmed" aria-label="Confirmed">{out}</section>;
}

function TrackingCard({ item, index }: { item: ClientWorkItem; index: number }) {
  const card = cardOfWorkItem(item);
  const video = card.delivered && item.deliveredUrl && /^https:\/\//i.test(item.deliveredUrl) ? item.deliveredUrl : null;
  return (
    <div
      className="wk wk-card fade-in"
      data-state={item.phase}
      data-node={card.node}
      data-work-item-id={item.id}
      style={{ animationDelay: `${Math.min(index * 0.035, 0.21)}s` }}
    >
      <a className="wk-row" href={card.href} aria-label={`Open ${item.title || "brief"} on the board.`}>
        <Avatar editorFirstName={item.editorFirstName} searching={card.searching} delivered={card.delivered} />
        <span className="wk-main">
          <span className="wk-title">{item.title || "Brief"}</span>
          <span className="wk-line">{card.line}</span>
        </span>
        <span className="wk-right">
          <span className="wk-chip">{card.chip}</span>
        </span>
      </a>
      <ol className="wk-rail" aria-label="Progress">
        {NODES.map((label, i) => {
          const n = (i + 1) as 1 | 2 | 3;
          const cls = n < card.node ? "done" : n === card.node ? "now" : "";
          return (
            <li className={`wk-node ${cls}`} key={label}>
              <span className="wk-dot" aria-hidden="true" />
              <span className="wk-node-label">{label}</span>
            </li>
          );
        })}
      </ol>
      {video && (
        <a className="wk-video" href={video} target="_blank" rel="noopener noreferrer">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 4h14v12H5z" /><path d="M10 8l5 2-5 2z" fill="currentColor" stroke="none" /><path d="M8 20h8" />
          </svg>
          View delivery
        </a>
      )}
    </div>
  );
}

/**
 * The left marker: a monogram once someone is on it, a status glyph otherwise.
 * `GET /api/work` never sends an editor photo (it is not in the contract),
 * so this is a monogram or nothing - never a broken-image fallback to design.
 */
function Avatar({ editorFirstName, searching, delivered }: { editorFirstName: string | null; searching: boolean; delivered: boolean }) {
  if (searching) {
    return <span className="wk-av searching" aria-hidden="true"><DispatchIcon /></span>;
  }
  const named = !!editorFirstName;
  if (!named && !delivered) {
    return <span className="wk-av" aria-hidden="true"><ClockIcon /></span>;
  }
  return (
    <span className={`wk-av${delivered && !named ? " done" : ""}`} aria-hidden="true" style={named ? { background: avatarColor(editorFirstName) } : undefined}>
      {editorFirstName
        ? <span className="mono">{avatarInitial(editorFirstName)}</span>
        : delivered ? <CheckIcon /> : <DispatchIcon />}
      {delivered && named && <span className="proj-badge"><CheckIcon /></span>}
    </span>
  );
}

function DispatchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="2" /><path d="M7.8 7.8a6 6 0 0 0 0 8.4" /><path d="M16.2 16.2a6 6 0 0 0 0-8.4" />
      <path d="M4.9 4.9a10 10 0 0 0 0 14.2" /><path d="M19.1 19.1a10 10 0 0 0 0-14.2" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><path d="M12 7.5V12l3 2" />
    </svg>
  );
}
function ReceiptIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" /><path d="M9 8h6M9 12h6" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}
